/**
 * Options for phone redaction.
 */
export type PhoneRedactorOptions = {
  /**
   * Number of digits to leave visible (from the end of the string).
   * @default 4
   */
  visibleDigits?: number;
  /**
   * Character to use for redaction.
   * @default '*'
   */
  redactionCharacter?: string;
  /**
   * If true, redact only if the number of digits in the phone number is greater than `visibleDigits`.
   * Otherwise, return the original string (no redaction).
   * @default false
   */
  redactIfShorterThanVisible?: boolean;
};

/**
 * Pure function to redact (mask) a phone number while preserving formatting.
 */
export const redactPhoneNumber = (phoneNumber: string, options: PhoneRedactorOptions = {}) => {
  const { visibleDigits = 4, redactionCharacter = '*', redactIfShorterThanVisible = false } = options;

  if (!phoneNumber) {
    return '';
  }

  const input = phoneNumber.trim();

  if (!input) {
    return input;
  }

  const allDigits = input.replace(/\D/g, '');

  if (!allDigits) {
    return input;
  }

  if (!redactIfShorterThanVisible && allDigits.length <= visibleDigits) {
    return input;
  }

  let digitsToMask = allDigits.length - visibleDigits;

  if (redactIfShorterThanVisible && allDigits.length <= visibleDigits) {
    digitsToMask = allDigits.length;
  }

  return input.replace(/\d/g, (d) => {
    if (digitsToMask > 0) {
      digitsToMask -= 1;
      return redactionCharacter;
    }

    return d;
  });
};
